// ============================================================
// Hirafi — Module devis : template preview + print CSS
//
// Ce composant s'affiche à l'écran ET à l'impression.
// window.print() génère un PDF fidèle sans dépendance externe.
// ============================================================

import { forwardRef } from "react";
import type { StandaloneDevisFormData } from "../types";
import { formatAmount } from "../lib/calculations";
import { DELAY_OPTIONS } from "../types";

interface Props {
  data: StandaloneDevisFormData;
  quoteNumber?: string;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("fr-DZ", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export const DevisPreviewPrint = forwardRef<HTMLDivElement, Props>(
  ({ data, quoteNumber }, ref) => {
    const delayLabel = DELAY_OPTIONS.find((o) => o.value === data.estimatedDelay)?.label;
    const today = new Date().toLocaleDateString("fr-DZ", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    return (
      <div
        ref={ref}
        id="devis-print-root"
        className="bg-white text-gray-900 font-sans"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        <div className="max-w-2xl mx-auto p-6 sm:p-10">
          {/* ── En-tête ── */}
          <div className="flex items-start justify-between mb-10">
            <div>
              <p className="text-3xl font-bold tracking-tight text-gray-900">DEVIS</p>
              {quoteNumber && (
                <p className="text-sm text-gray-500 mt-1">N° {quoteNumber}</p>
              )}
              <p className="text-sm text-gray-500 mt-0.5">Émis le {today}</p>
            </div>
            <div className="text-right">
              <p
                className="text-xl font-bold"
                style={{ color: "#E0561A" }}
              >
                Hirafi
              </p>
              <p className="text-xs text-gray-400 mt-0.5">hirafidz.com</p>
            </div>
          </div>

          {/* ── Artisan / Client ── */}
          <div className="grid grid-cols-2 gap-8 mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">
                De
              </p>
              <p className="font-semibold text-gray-900">{data.artisan.name}</p>
              {data.artisan.activity && (
                <p className="text-sm text-gray-600">{data.artisan.activity}</p>
              )}
              {data.artisan.phone && (
                <p className="text-sm text-gray-600">{data.artisan.phone}</p>
              )}
              {data.artisan.email && (
                <p className="text-sm text-gray-600">{data.artisan.email}</p>
              )}
              {data.artisan.address && (
                <p className="text-sm text-gray-600">{data.artisan.address}</p>
              )}
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">
                À l'attention de
              </p>
              <p className="font-semibold text-gray-900">{data.client.name}</p>
              {data.client.phone && (
                <p className="text-sm text-gray-600">{data.client.phone}</p>
              )}
              {data.client.email && (
                <p className="text-sm text-gray-600">{data.client.email}</p>
              )}
              {data.client.address && (
                <p className="text-sm text-gray-600">{data.client.address}</p>
              )}
            </div>
          </div>

          {/* ── Titre + description ── */}
          <div className="mb-8">
            <h1 className="text-lg font-bold text-gray-900 mb-2">{data.title}</h1>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
              {data.description}
            </p>
          </div>

          {/* ── Lignes de prestation ── */}
          {data.lineItems.length > 0 && (
            <div className="mb-8">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-2 font-semibold text-gray-700">Prestation</th>
                    <th className="text-center py-2 font-semibold text-gray-700 w-16">Qté</th>
                    <th className="text-right py-2 font-semibold text-gray-700 w-28">
                      Prix unit.
                    </th>
                    <th className="text-right py-2 font-semibold text-gray-700 w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lineItems.map((item, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 text-gray-800">{item.label}</td>
                      <td className="py-2 text-center text-gray-600">{item.quantity}</td>
                      <td className="py-2 text-right text-gray-600">
                        {formatAmount(item.unit_price, data.currency)}
                      </td>
                      <td className="py-2 text-right font-medium text-gray-800">
                        {formatAmount(item.total_price, data.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Total + TVA ── */}
          <div className="flex justify-end mb-8">
            <div className="w-64 space-y-1.5 text-sm">
              {data.vatRate ? (
                <>
                  <div className="flex justify-between text-gray-500 border-t pt-3">
                    <span>Montant HT</span>
                    <span>{formatAmount(data.totalAmount, data.currency)}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>TVA ({data.vatRate}%)</span>
                    <span>{formatAmount(data.totalAmount * (data.vatRate / 100), data.currency)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold text-gray-700">Total TTC</span>
                    <span
                      className="text-lg font-bold"
                      style={{ color: "#E0561A" }}
                    >
                      {formatAmount(data.totalAmount * (1 + data.vatRate / 100), data.currency)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-sm text-gray-600 border-t pt-3">
                  <span className="font-semibold text-gray-700">Total</span>
                  <span
                    className="text-lg font-bold"
                    style={{ color: "#E0561A" }}
                  >
                    {formatAmount(data.totalAmount, data.currency)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Infos complémentaires ── */}
          <div className="grid grid-cols-2 gap-6 text-sm border-t pt-6 mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                Validité
              </p>
              <p className="text-gray-800">Jusqu'au {formatDate(data.validUntil)}</p>
            </div>
            {delayLabel && (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                  Délai d'intervention
                </p>
                <p className="text-gray-800">{delayLabel}</p>
              </div>
            )}
          </div>

          {/* ── Notes ── */}
          {data.notes && (
            <div className="border-t pt-6 mb-6">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">
                Notes
              </p>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.notes}</p>
            </div>
          )}

          {/* ── Pied de page ── */}
          <div className="border-t pt-4 text-center text-xs text-gray-400">
            Devis généré via Hirafi · hirafidz.com
          </div>
        </div>
      </div>
    );
  }
);

DevisPreviewPrint.displayName = "DevisPreviewPrint";
